import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'

import { BackgroundTypeGradientPreviewIcon } from '../icons'
import BackgroundImageSamplesSelection from './BackgroundImageSamplesSelection'
import HeaderBackgroundUpload from './HeaderBackgroundUpload'
import PillRadio from './radio/PillRadio'
import FieldError from 'components/fields/FieldError'
import FormRow from 'components/fields/FormRow'
import Legend from 'components/fields/Legend'
import { FormValues } from 'components/forms/ThemeForm'

const RadioWrapper = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space4};
	padding-right: ${({ theme }) => theme.spaces.space4};
`

const PillRadioInner = styled.span`
	position: relative;
	height: 115px;
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space2};
	background-color: ${({ theme }) => theme.colors.light.surface};
	border-radius: ${({ theme }) => theme.borderRadiuses.lg};
	padding: ${({ theme }) => theme.spaces.space5};
`

const RadioTitle = styled.span`
	${({ theme }) => theme.typography.caption};
`

const RadioDescription = styled.span`
	${({ theme }) => theme.typography.captionSmall};
	color: #6e6e6e;
	max-width: 180px;
`

const BackgroundPreview = styled.span<{ fill: string }>`
	position: absolute;
	bottom: 0;
	right: 20px;
	height: 69px;
	width: 100px;
	background: ${({ fill }) => fill};
	border-top-left-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	border-top-right-radius: ${({ theme }) => theme.borderRadiuses.xxl};
`

const GradientWrapper = styled.span<{ themeVariant: 'light' | 'dark' }>`
	filter: blur(14px);

	svg {
		height: 100%;
		width: 100%;
		--gradient-background-color: ${({ themeVariant }) =>
			themeVariant === 'dark' ? 'rgb(26, 26, 26)' : 'rgb(246, 246, 246)'};
	}
`

const BackgroundTypeSelection = () => {
	const { register, formState, watch, trigger } = useFormContext<FormValues>()
	const theme = useTheme()
	const error = formState.errors.headerBackgroundType?.message
	const currentPrimaryColor = watch('primaryColor')
	const currentThemeVariant = watch('themeVariant')
	const gradientColor = currentThemeVariant === 'dark' ? '#1c1919' : '#FFFFFF'

	const fieldProps = register('headerBackgroundType', {
		onChange: () => {
			trigger('headerBackgroundImage')
			trigger('headerBackgroundType')
		},
	})

	return (
		<>
			<Legend>Choose background</Legend>
			<FormRow>
				<RadioWrapper>
					<PillRadio value="solid" {...fieldProps}>
						<PillRadioInner>
							<RadioTitle>Solid</RadioTitle>
							<RadioDescription>Theme color used as a background</RadioDescription>
							<BackgroundPreview fill={currentPrimaryColor} />
						</PillRadioInner>
					</PillRadio>
					<PillRadio value="gradient" {...fieldProps}>
						<PillRadioInner>
							<RadioTitle>Gradient</RadioTitle>
							<RadioDescription>Colors automatically generated based on the theme color</RadioDescription>
							<BackgroundPreview fill={`linear-gradient(232deg, ${currentPrimaryColor} 7%, ${gradientColor} 89%)`}>
								<GradientWrapper themeVariant={currentThemeVariant}>
									<BackgroundTypeGradientPreviewIcon />
								</GradientWrapper>
							</BackgroundPreview>
						</PillRadioInner>
					</PillRadio>
					<PillRadio value="image" {...fieldProps}>
						<PillRadioInner>
							<RadioTitle>
								Image <RadioDescription>(720x600)</RadioDescription>
							</RadioTitle>
							<RadioDescription>Add your own file or pick from gallery</RadioDescription>
							<BackgroundImageSamplesSelection />
							<BackgroundPreview fill={theme.colors.light.surfaceVariant}>
								<HeaderBackgroundUpload />
							</BackgroundPreview>
						</PillRadioInner>
					</PillRadio>
				</RadioWrapper>
				{error && <FieldError>{error}</FieldError>}
			</FormRow>
		</>
	)
}

export default BackgroundTypeSelection
